import { UniButton } from '@/design-system/components/buttons';
import { UniIconButton } from '@/design-system/components/buttons';
import { TextCaption, TextTitle } from '@/design-system/components/typography';
import { SafeAreaView, ScrollView, SxProp, View, useDripsyTheme } from 'dripsy';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UniInputAmount } from '@/design-system/components/inputs';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { FixedLimitStackParamList, FixedLimitStackRoutes } from '@/features/account-management/screens/fixed-limit/type';
import { PageHeaderModalNative } from '@/features/account-management/screens/page/PageHeaderModalNative';
import { formatAmountByDecimal } from '../../utils';

export interface FixedLimitProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  onData: (data: any) => void;
  func?: (limit: number, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
  record?: any;
}

export const FixedLimitNativeZeroScreen: React.FC<FixedLimitProps> = (props) => {
  const { onClose, onLayout, onData, func, onReturnData, refresh, record } = props;
  const { t } = useTranslation('account-management');
  const { container, body, textMb, btnView, btn, flexContainer, summaryContent } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<FixedLimitStackParamList>>();

  const [fixedLimit, setFixedLimit] = useState<any>({
    limit: undefined,
    name: record?.name,
    currency: record?.currency
  });
  const [errorMsg, setErrorMsg] = useState<any>({ limit: undefined });

  const validateForm = async (data?: { [key: string]: string | undefined }) => {
    const keys = data ? Object.keys(data) : [];
    const keysLength = keys.length;
    let validateFlag = true;
    let errorMsgFlag = { ...errorMsg };

    if ((!keysLength && !fixedLimit.limit) || (keys.includes('limit') && !data?.limit)) {
      errorMsgFlag.limit = t('card-account-management::glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && fixedLimit.limit) || (keys.includes('limit') && data?.limit)) {
      errorMsgFlag.limit = undefined;
    }

    setErrorMsg(errorMsgFlag);
    return validateFlag;
  };

  const changeFixedLimit = async () => {
    const validate = await validateForm();
    if (validate) {
      onData({ data: fixedLimit });
      navigation.navigate(FixedLimitStackRoutes.Confirm, { onLayout, data: fixedLimit, func, onReturnData, refresh });
    }
  };

  const { theme } = useDripsyTheme();

  return (
    <SafeAreaView sx={container}>
      <ScrollView showsVerticalScrollIndicator={false}>
        <View sx={{ backgroundColor: '$backgroundPrimary', width: '100%' }}>
          <PageHeaderModalNative>
            <View sx={{ px: '$4', mt: '$5', mb: '$4' }}>
              <View sx={{ position: 'absolute', top: 0, left: 12, zIndex: 2 }}>
                <UniIconButton
                  icon="arrowLeft"
                  options={{ iconStyles: { color: theme?.colors?.$white as string } }}
                  onPress={onClose}></UniIconButton>
              </View>
              <View sx={{ textAlign: 'center' }}>
                <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
                  {t('card-account-management::fixed-limit.title--fixed-limit')}
                </TextTitle>
              </View>
            </View>
          </PageHeaderModalNative>
          <View sx={body}>
            <TextTitle sx={textMb} semiBold>
              {t('card-account-management::fixed-limit.form-text--fill-out-form-to-fixed-limit')}
            </TextTitle>
            <TextCaption semiBold label>
              {t('card-account-management::fixed-limit.title-credit-summary')}
            </TextCaption>
            <View sx={flexContainer}>
              <View sx={summaryContent}>
                <TextCaption>{t('card-account-management::fixed-limit.text-funding-account')}</TextCaption>
                <TextCaption semiBold>{record?.creditSummary?.accountName ?? ''}</TextCaption>
              </View>
              <View sx={summaryContent}>
                <TextCaption>{t('card-account-management::fixed-limit.text-total-credit')}</TextCaption>
                <TextCaption semiBold>{formatAmountByDecimal({ value: record?.creditSummary?.totalCredit, suffix: record?.currency, decimals: 2 })}</TextCaption>
              </View>
              <View sx={summaryContent}>
                <TextCaption>{t('card-account-management::fixed-limit.text-total-assigned-credit')}</TextCaption>
                <TextCaption semiBold>{formatAmountByDecimal({ value: record?.creditSummary?.totalAssignedCredit, suffix: record?.currency, decimals: 2 })}</TextCaption>
              </View>
              <View sx={summaryContent}>
                <TextCaption>{t('card-account-management::fixed-limit.text-available-credit')}</TextCaption>
                <TextCaption semiBold>{formatAmountByDecimal({ value: record?.creditSummary?.availableCredit, suffix: record?.currency, decimals: 2 })}</TextCaption>
              </View>
            </View>
            <View sx={{ marginTop: '$4', gap: '$5' }}>
              <UniInputAmount
                labelText={t('card-account-management::fixed-limit.form-text--limit')}
                assetPrefix={false}
                maxFlag={false}
                sxWrap={{ alignItems: 'center', flexDirection: 'row-reverse' }}
                asset={fixedLimit.currency}
                errorText={errorMsg.limit}
                value={fixedLimit.limit}
                decimals={2}
                onValueChange={(value) => {
                  setFixedLimit({
                    ...fixedLimit,
                    limit: value
                  });
                  validateForm({ limit: value?.toString() });
                }}
              />
            </View>
            <View sx={btnView}>
              <UniButton
                sx={btn}
                labelSx={{ color: '$balck' }}
                label={t('card-account-management::change-atm-pin.button--update')}
                onPress={changeFixedLimit}
              />
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    height: '100%',
    width: '100%',
    backgroundColor: '$backgroundPrimary',
    zIndex: -100
  },
  body: {
    px: 16,
    py: 20
  },
  textMb: {
    marginBottom: 20
  },
  btnView: {
    width: '100%',
    left: 0,
    bottom: 0,
    pt: '$10'
  },
  btn: {
    width: '100%',
    height: 44
  },
  flexContainer: {
    flexDirection: 'column',
    backgroundColor: '$backgroundSecondary',
    borderRadius: 10,
    padding: 16,
    gap: 4
  },
  summaryContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  }
};
